// ── DESIGN TOKENS (Light Theme) ──
// Source of truth: hemologix-style-guide-light.jsx (v1.1 audited)
// All quality module components import from this file.

export const T = {
  // Core palette
  bg: '#FAFAFA',
  bgSoft: '#F4F4F5',
  bgCard: '#FFFFFF',
  bgElevated: '#FFFFFF',
  bgInput: '#F0F0F2',

  // Text
  text: '#18181B',
  textSoft: '#52525B',
  textMuted: '#71717A',
  textGhost: '#A1A1AA',

  // Accent
  accent: '#E11D48',
  accentSoft: 'rgba(225,29,72,0.08)',
  accentGlow: 'rgba(225,29,72,0.18)',

  // Cherry
  cherry: '#9F1239',
  cherrySoft: 'rgba(159,18,57,0.06)',

  // Gold
  gold: '#92700C',
  goldSoft: 'rgba(146,112,12,0.08)',

  // Status
  success: '#16A34A',
  successSoft: 'rgba(22,163,74,0.08)',
  warning: '#CA8A04',
  warningSoft: 'rgba(202,138,4,0.08)',
  danger: '#DC2626',
  dangerSoft: 'rgba(220,38,38,0.08)',
  info: '#2563EB',
  infoSoft: 'rgba(37,99,235,0.08)',

  // Borders
  border: '#E4E4E7',
  borderSoft: '#F0F0F2',
  borderStrong: '#D4D4D8',

  // Shadows
  shadowSm: '0 1px 2px rgba(0,0,0,0.05)',
  shadowMd: '0 2px 8px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
  shadowLg: '0 4px 16px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.04)',
  shadowXl: '0 8px 32px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.04)',
  shadowAccent: '0 4px 20px rgba(225,29,72,0.2)',

  // Radii
  radius: { sm: 6, md: 10, lg: 14, xl: 20, full: 999 } as const,

  // Spacing scale
  space: [0, 4, 8, 12, 16, 20, 24, 32, 40, 48, 64, 80, 96, 128] as const,

  // Font sizes
  fontSize: {
    xs: 11, sm: 13, base: 15, md: 17, lg: 20, xl: 24,
    '2xl': 30, '3xl': 38, '4xl': 48, display: 56,
  } as const,
};

// Font stacks
export const F = {
  head: "'Cormorant Garamond', Georgia, serif",
  body: "'DM Sans', -apple-system, sans-serif",
  mono: "'SF Mono', 'Fira Code', monospace",
};

// Module identity (colors + monogram abbreviations)
export const modules = {
  hub:    { name: 'Quality Platform', color: '#E11D48', soft: 'rgba(225,29,72,0.06)', abbr: 'QP' },
  twin:   { name: 'Twin Fighter',    color: '#DC2626', soft: 'rgba(220,38,38,0.06)', abbr: 'TF' },
  doc:    { name: 'Doc vs AI',       color: '#0D9488', soft: 'rgba(13,148,136,0.06)', abbr: 'DA' },
  teach:  { name: 'Teach the Nurse', color: '#16A34A', soft: 'rgba(22,163,74,0.06)', abbr: 'TN' },
  nurse:  { name: 'Nurse Chat Test', color: '#D97706', soft: 'rgba(217,119,6,0.06)', abbr: 'NC' },
  viewer: { name: 'AI Twin Viewer',  color: '#7C3AED', soft: 'rgba(124,58,237,0.06)', abbr: 'AV' },
  plate:  { name: 'Fashion Plate',  color: '#8B5CF6', soft: 'rgba(139,76,246,0.06)', abbr: 'FP' },
  chat:   { name: 'Patient Chat',   color: '#E11D48', soft: 'rgba(225,29,72,0.06)', abbr: 'PC' },
} as const;

export type ModuleKey = keyof typeof modules;

// ── Shared utilities ──

/** Returns T.success / T.warning / T.danger based on score thresholds */
export function scoreColor(score: number): string {
  return score >= 70 ? T.success : score >= 50 ? T.warning : T.danger;
}

/** Returns the soft background variant of scoreColor */
export function scoreBgColor(score: number): string {
  return score >= 70 ? T.successSoft : score >= 50 ? T.warningSoft : T.dangerSoft;
}

// ── Shared style constants ──

export const S = {
  flexCenter: { display: 'flex', alignItems: 'center', justifyContent: 'center' } as const,
  flexBetween: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' } as const,
  textTruncate: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const,
  sectionLabel: {
    fontFamily: F.mono, fontSize: T.fontSize.xs, fontWeight: 600,
    letterSpacing: 2, textTransform: 'uppercase',
  } as const,
  card: {
    background: T.bgCard, border: `1px solid ${T.border}`,
    boxShadow: T.shadowSm, borderRadius: T.radius.md,
  } as const,
};
